/** @format */

import { Component, ElementRef, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild, SimpleChanges } from '@angular/core';
import { fromEvent, Observable, Subscription } from 'rxjs';

@Component({
    selector: 'app-pagination-control',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    standalone: false
})
export class Pagination implements OnInit, OnChanges {
	@Input() contentPerPage!: number;
	@Input() totalContentLength!: number;
	@Output() onPaginationUpdate = new EventEmitter<number>();

	DisplayModes = Object.freeze({
		Full: 'full',
		Condensed: 'condensed',
	});

	DisplayStyles = Object.freeze({
		Inline: 'inline',
		Stacked: 'stacked',
	});

	DisplayedList = Object.freeze({
		Initial: 'initial',
		Middle: 'middle',
		Final: 'final',
	});

	canRenderBtnsAndPageNumbers = false;
	paginationNumbersFreeWidth = 0;
	paginationCurrentPage = 1;
	paginationStartingPosition = 1;
	paginationLastPage = 1;
	paginationKeysStart: number[] = [];
	paginationKeysMiddle: number[] = [];
	paginationKeysEnd: number[] = [];

	currentListDisplayed: string = this.DisplayedList.Initial;
	paginationNumberListSize: string = this.DisplayModes.Full;
	paginationDisplayStyle: string = this.DisplayStyles.Inline;

	shouldDisableNextBtns = false;
	shouldDisablePrevBtns = false;

	@ViewChild('paginationControl') paginationControl!: ElementRef;

	resizeObservable$!: Observable<Event>;
	resizeSubscription$!: Subscription;

	constructor() {
		this.resizeObservable$ = fromEvent(window, 'resize');
		this.resizeSubscription$ = this.resizeObservable$.subscribe(() => this.handlePaginationResizing());
	}

	ngOnInit(): void {
		const contentLoadInterval = setInterval(() => {
			if (this.totalContentLength > 0) {
				this.paginationLastPage = Math.ceil(this.totalContentLength / this.contentPerPage);

				if (this.paginationControl) {
					this.handlePaginationResizing();
					this.goToFirstPage();
					clearInterval(contentLoadInterval);
				}
			}
		}, 100);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.totalContentLength && !changes.totalContentLength.firstChange) {
			if (this.totalContentLength === 0) {
				this.paginationLastPage = 1;
			} else {
				this.paginationLastPage = Math.ceil(this.totalContentLength / this.contentPerPage);
			}

			if (this.paginationControl) {
				this.buildPaginationRenderingKeys();
				this.goToFirstPage();
			}
		}
	}

	handlePaginationResizing() {
		const paginationControlWidth = this.paginationControl?.nativeElement?.offsetWidth || 0;

		// pixel approximations for pagination long buttons
		const stockButtonSizes = 92 + 120 + 92 + 92;

		// this is needed as on resize, the moment the referenced elements are no longer visable,
		// the value of their combined width is no longer taken into account causing render issues
		// as the code will view the space available as ok to show the buttons once again
		const btnRenderSizePx = stockButtonSizes;

		const numbersControlWidth = Math.max(0, paginationControlWidth - btnRenderSizePx);

		// check the icons width for a minimum of 5 icons each at 44px in width
		const paginationIconsMinWidth = 5 * 44;

		// check whether we can render the list with the text buttons if possible
		const canRenderFullPageList = btnRenderSizePx + paginationIconsMinWidth <= paginationControlWidth;

		this.canRenderBtnsAndPageNumbers = canRenderFullPageList;
		this.paginationNumbersFreeWidth = canRenderFullPageList ? numbersControlWidth : paginationControlWidth;
		this.buildPaginationRenderingKeys();
	}

	buildPaginationRenderingKeys() {
		// determing how many numbers are possible to render on screen
		const pageNumberSizePx = 44;
		const numButtonsPlaceable = Math.floor(this.paginationNumbersFreeWidth / pageNumberSizePx);

		const canDisplayFullNumbersList = numButtonsPlaceable >= 7;

		// set the number of keys maximum for the edge key arrays
		const edgeKeysLimit = canDisplayFullNumbersList ? 5 : this.paginationLastPage < 5 ? this.paginationLastPage : 3;
		let initialArrayKeys: number[] = [];
		let middleArrayKeys: number[] = [];
		let endArrayKeys: number[] = [];

		// set whether or not we show the full numbers list with 7 values displayed total (5 + ellipses/edges)
		this.paginationNumberListSize = canDisplayFullNumbersList ? this.DisplayModes.Full : this.DisplayModes.Condensed;
		this.paginationDisplayStyle = this.canRenderBtnsAndPageNumbers ? this.DisplayStyles.Inline : this.DisplayStyles.Stacked;

		// build the arrays of keys to house the page numbers
		if (this.paginationLastPage <= edgeKeysLimit) {
			// the number of pages can fit on a normal pagination list display
			initialArrayKeys = Array.from({ length: this.paginationLastPage }, (_, index) => index + 1);
		} else {
			// pages can fit on first and final page keys array
			if (this.paginationLastPage <= edgeKeysLimit * 2) {
				initialArrayKeys = Array.from({ length: Math.ceil(this.paginationLastPage / 2) }, (_, index) => index + 1);

				endArrayKeys = Array.from(
					{ length: Math.floor(this.paginationLastPage / 2) },
					(_, index) => this.paginationLastPage - Math.floor(this.paginationLastPage / 2) + index + 1
				);
			} else {
				initialArrayKeys = Array.from({ length: edgeKeysLimit }, (_, index) => index + 1);

				middleArrayKeys = Array.from({ length: this.paginationLastPage - 2 * edgeKeysLimit }, (_, index) => index + 1 + edgeKeysLimit);

				endArrayKeys = Array.from({ length: edgeKeysLimit }, (_, index) => this.paginationLastPage - edgeKeysLimit + index + 1);
			}
		}

		this.paginationKeysStart = initialArrayKeys;
		this.paginationKeysMiddle = middleArrayKeys;
		this.paginationKeysEnd = endArrayKeys;

		this.updateListDisplayed();
	}

	updateListDisplayed() {
		const isPageInInitial = this.paginationKeysStart.includes(this.paginationCurrentPage);
		const isPageInMiddle = this.paginationKeysMiddle.includes(this.paginationCurrentPage);
		this.currentListDisplayed = isPageInInitial
			? this.DisplayedList.Initial
			: isPageInMiddle
			? this.DisplayedList.Middle
			: this.DisplayedList.Final;
	}

	handlePaginationUpdate(newStartingPosition: number, newPageNumber: number) {
		this.paginationCurrentPage = newPageNumber;
		this.paginationStartingPosition = newStartingPosition;
		this.updateListDisplayed();
		this.onPaginationUpdate.emit(newStartingPosition);
		this.shouldDisableNextBtns = this.shouldDisableNextPageBtns();
		this.shouldDisablePrevBtns = this.shouldDisablePrevPageBtns();
	}

	goToSpecificPage(pageNumber: number) {
		const newStartingPosition = (pageNumber - 1) * this.contentPerPage + 1;
		this.handlePaginationUpdate(newStartingPosition, pageNumber);
	}

	goToFirstPage() {
		this.handlePaginationUpdate(1, 1);
	}

	goToPrevPage() {
		const newStartingPosition = this.paginationStartingPosition - this.contentPerPage;
		this.handlePaginationUpdate(newStartingPosition, this.paginationCurrentPage - 1);
	}

	goToNextPage() {
		const newStartingPosition = this.paginationStartingPosition + this.contentPerPage;
		this.handlePaginationUpdate(newStartingPosition, this.paginationCurrentPage + 1);
	}

	goToLastPage() {
		const startingBookmarkPosition = (this.paginationLastPage - 1) * this.contentPerPage + 1;
		this.handlePaginationUpdate(startingBookmarkPosition, this.paginationLastPage);
	}

	shouldDisablePrevPageBtns = () => this.paginationCurrentPage === 1;
	shouldDisableNextPageBtns() {
		return this.contentPerPage === 0 ? true : this.paginationCurrentPage === Math.ceil(this.totalContentLength / this.contentPerPage);
	}
}
